/*
  The Chocolate Architect — Build-a-Box revenue quick-win progressive enhancement draft.
  Draft/dev only. Does not replace /cart/add.js, variant IDs, form actions, analytics, checkout,
  or fulfillment-facing line-item properties. Enhances the existing .custom-box-builder DOM.
*/
(function () {
  'use strict';

  var PRICE_BY_SIZE = {
    '16': '$46.95',
    '32': '$68.95',
    '48': '$88.95'
  };

  var SIZE_MESSAGING = {
    '16': { label: '16 pieces', badge: 'Small gift', sub: 'Personal treat, thank-you, or tasting box' },
    '32': { label: '32 pieces', badge: 'Gift favorite', sub: 'A generous box for birthdays, hosts, and families' },
    '48': { label: '48 pieces', badge: 'Best value', sub: 'Event-ready, shareable, and most impressive' }
  };

  var ASSORTMENTS = [
    {
      name: 'Customer Favorites',
      note: 'Classic, easy-to-gift Chocolate Silk flavors.',
      flavors: ['Chocolate Silk', 'Salted Caramel Silk', 'Dark Raspberry Silk', 'Peanut Butter Silk']
    },
    {
      name: 'Wine Night Mix',
      note: 'Dark, berry, caramel, and nut-forward picks.',
      flavors: ['Dark Chocolate', 'Dark Raspberry Silk', 'Salted Caramel Silk', 'Almond Rococo Silk']
    },
    {
      name: 'Caramel + Nut Mix',
      note: 'Rich, cozy flavors for hosts and thank-yous.',
      flavors: ['Caramel Turtle Silk', 'Caramel Espresso Silk', 'Salted Caramel Silk', 'Amaretto Almond Silk']
    },
    {
      name: 'Dessert Party Mix',
      note: 'Fun cake-and-cookie inspired flavors.',
      flavors: ['Black Forest Cake Silk', 'Red Velvet Cake Silk', 'Carrot Cake Silk', 'Confetti Cake Silk']
    },
    {
      name: 'Dark Chocolate Lovers',
      note: 'A deeper profile for serious chocolate fans.',
      flavors: ['Dark Chocolate', 'Dark Raspberry Silk', 'Dark Lavender Silk', 'Dark Peanut Butter Silk']
    }
  ];

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html) node.innerHTML = html;
    return node;
  }

  function text(node) {
    return node ? (node.textContent || '').trim() : '';
  }

  function getSize(container) {
    var select = container.querySelector('#box-size-select');
    return select && select.value ? select.value : '16';
  }

  function getPrice(container) {
    var size = getSize(container);
    var select = container.querySelector('#box-size-select');
    if (select && select.selectedOptions && select.selectedOptions[0]) {
      var match = select.selectedOptions[0].textContent.match(/\$[\d,.]+/);
      if (match) return match[0];
    }
    return PRICE_BY_SIZE[size] || '';
  }

  function getMax(container) {
    var label = container.querySelector('#max-flavors');
    var parsed = label ? parseInt(label.textContent, 10) : NaN;
    if (!Number.isNaN(parsed)) return parsed;
    return getSize(container) === '16' ? 4 : 6;
  }

  function getSelectedChips(container) {
    return Array.prototype.slice.call(container.querySelectorAll('#cb-selected .cb-chip')).map(function (chip) {
      return text(chip).replace(/[×x]\s*$/i, '').trim();
    }).filter(Boolean);
  }

  function getSelectedCount(container) {
    return getSelectedChips(container).length;
  }

  function getAddButton(container) {
    return container.querySelector('#add-to-cart-btn');
  }

  function announce(container, message) {
    var notice = container.querySelector('#cb-notice');
    if (!notice) return;
    notice.textContent = message;
    notice.classList.add('is-visible');
    window.clearTimeout(notice.__tcaNoticeTimer);
    notice.__tcaNoticeTimer = window.setTimeout(function () {
      notice.classList.remove('is-visible');
    }, 2600);
  }

  function clearCurrentSelections(container) {
    Array.prototype.forEach.call(container.querySelectorAll('#flavor-grid input[type="checkbox"][data-flavor]:checked'), function (input) {
      input.checked = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function selectFlavor(container, flavorName) {
    var input = container.querySelector('#flavor-grid input[type="checkbox"][data-flavor="' + window.CSS.escape(flavorName) + '"]');
    if (!input || input.disabled) return false;
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  function applyAssortment(container, assortment) {
    clearCurrentSelections(container);
    var max = getMax(container);
    var available = [];
    assortment.flavors.slice(0, max).forEach(function (flavor) {
      if (selectFlavor(container, flavor)) available.push(flavor);
    });
    updateAll(container);
    if (available.length) {
      announce(container, assortment.name + ' selected. Review your flavors or swap any card.');
    } else {
      announce(container, 'Those flavors were not found in the current flavor list.');
    }
  }

  function addHero(container) {
    var inner = container.querySelector('.cb-container');
    if (!inner || inner.querySelector('.tca-bab-hero')) return;

    var hero = el('div', 'tca-bab-hero',
      '<div class="tca-bab-hero-copy">' +
        '<div class="tca-bab-kicker">Build a custom Chocolate Silk gift box</div>' +
        '<h2>Choose the pieces. We craft the moment.</h2>' +
        '<p>Pick a box size, choose their favorite flavors, and create a premium Chocolate Architect gift that feels personal from the first bite.</p>' +
        '<div class="tca-bab-trust-row" aria-label="Why customers choose Build-a-Box">' +
          '<span class="tca-bab-pill">Handcrafted chocolate</span>' +
          '<span class="tca-bab-pill">Gift-ready presentation</span>' +
          '<span class="tca-bab-pill">Folsom artisan brand</span>' +
          '<span class="tca-bab-pill">Wine-night friendly</span>' +
        '</div>' +
      '</div>' +
      '<aside class="tca-bab-gift-card" aria-label="Gift guidance">' +
        '<strong>Sending a gift?</strong>' +
        '<span>Choose 32 pieces for a generous gift or 48 pieces for hosts, celebrations, teams, and client thank-yous.</span>' +
      '</aside>'
    );

    var progress = el('div', 'tca-bab-progress',
      '<div class="tca-bab-progress-track" aria-label="Build-a-Box progress">' +
        '<div class="tca-bab-progress-step"><span class="tca-bab-progress-dot">1</span><span>Box size</span></div>' +
        '<div class="tca-bab-progress-step"><span class="tca-bab-progress-dot">2</span><span>Shortcut</span></div>' +
        '<div class="tca-bab-progress-step"><span class="tca-bab-progress-dot">3</span><span>Flavors</span></div>' +
        '<div class="tca-bab-progress-step"><span class="tca-bab-progress-dot">4</span><span>Review</span></div>' +
      '</div>'
    );

    inner.insertBefore(progress, inner.firstChild);
    inner.insertBefore(hero, progress);
  }

  function addSizeCards(container) {
    var select = container.querySelector('#box-size-select');
    var optionsWrap = container.querySelector('.cb-options');
    if (!select || !optionsWrap || optionsWrap.querySelector('.tca-bab-size-cards')) return;

    var group = el('div', 'tca-bab-card-group tca-bab-size-cards');
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Choose a box size');

    Array.prototype.forEach.call(select.options, function (option) {
      var size = option.value;
      var msg = SIZE_MESSAGING[size] || { label: option.textContent, badge: '', sub: '' };
      var priceMatch = option.textContent.match(/\$[\d,.]+/);
      var price = priceMatch ? priceMatch[0] : '';
      var card = el('button', 'tca-bab-choice',
        '<span class="tca-bab-choice-badge">' + msg.badge + '</span>' +
        '<span class="tca-bab-choice-main">' + msg.label + '</span>' +
        '<span class="tca-bab-choice-price">' + price + '</span>' +
        '<span class="tca-bab-choice-sub">' + msg.sub + '</span>'
      );
      card.type = 'button';
      card.dataset.value = size;
      card.setAttribute('role', 'radio');
      card.addEventListener('click', function () {
        select.value = size;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        updateAll(container);
      });
      group.appendChild(card);
    });

    select.classList.add('tca-bab-select-backup');
    optionsWrap.appendChild(group);
  }

  function addAssortments(container) {
    var inner = container.querySelector('.cb-container');
    var search = inner && inner.querySelector('.cb-flavor-search');
    if (!inner || !search || inner.querySelector('.tca-bab-assortments')) return;

    var block = el('section', 'tca-bab-assortments');
    block.setAttribute('aria-labelledby', 'tca-bab-assortments-title');
    block.innerHTML =
      '<div class="tca-bab-section-heading">' +
        '<div class="tca-bab-step-label">Need a shortcut?</div>' +
        '<h3 id="tca-bab-assortments-title">Start with a guided assortment</h3>' +
        '<p>Use a curated starter mix, then swap any flavors before adding to cart.</p>' +
      '</div>' +
      '<div class="tca-bab-assortment-grid"></div>';

    var grid = block.querySelector('.tca-bab-assortment-grid');
    ASSORTMENTS.forEach(function (assortment) {
      var button = el('button', 'tca-bab-assortment',
        '<span class="tca-bab-assortment-name">' + assortment.name + '</span>' +
        '<span class="tca-bab-assortment-note">' + assortment.note + '</span>' +
        '<span class="tca-bab-assortment-list">' + assortment.flavors.join(' • ') + '</span>'
      );
      button.type = 'button';
      button.addEventListener('click', function () { applyAssortment(container, assortment); });
      grid.appendChild(button);
    });

    inner.insertBefore(block, search);
  }

  function addGiftGuidance(container) {
    var inner = container.querySelector('.cb-container');
    var search = inner && inner.querySelector('.cb-flavor-search');
    if (!inner || !search || inner.querySelector('.tca-bab-guidance')) return;
    var guidance = el('div', 'tca-bab-guidance',
      '<div class="tca-bab-guidance-card"><strong>Want variety?</strong><span>Choose 3–4 flavors for a balanced box and easy gifting.</span></div>' +
      '<div class="tca-bab-guidance-card"><strong>Know their favorite?</strong><span>One-flavor boxes are supported; the builder will ask you to confirm.</span></div>' +
      '<div class="tca-bab-guidance-card"><strong>Make it generous.</strong><span>32 and 48-piece boxes feel more substantial for celebrations and thank-yous.</span></div>'
    );
    inner.insertBefore(guidance, search.nextSibling);
  }

  function addMobileSummary(container) {
    if (container.querySelector('.tca-bab-mobile-summary')) return;
    var bar = el('div', 'tca-bab-mobile-summary',
      '<div class="tca-bab-mobile-copy"><strong>Build-a-Box</strong><span class="tca-bab-mobile-state">Choose at least 1 flavor</span></div>' +
      '<button type="button" class="tca-bab-mobile-cta">Choose flavors</button>'
    );
    bar.setAttribute('aria-live', 'polite');
    container.appendChild(bar);
    bar.querySelector('.tca-bab-mobile-cta').addEventListener('click', function () {
      var addBtn = getAddButton(container);
      if (addBtn && !addBtn.disabled) addBtn.click();
      else (container.querySelector('#flavor-grid') || container).scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function optimizeThumbnails(container) {
    Array.prototype.forEach.call(container.querySelectorAll('.flavor-thumb'), function (img) {
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 96;
      img.height = 96;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
    });
    var preview = container.querySelector('#box-preview-img');
    if (preview) {
      preview.decoding = 'async';
      preview.width = preview.width || 240;
      preview.height = preview.height || 180;
    }
  }

  function cleanupAccessibility(container) {
    Array.prototype.forEach.call(container.querySelectorAll('.flavor-item'), function (card) {
      var input = card.querySelector('input[type="checkbox"][data-flavor]');
      var label = card.querySelector('label');
      if (!input) return;
      var flavor = input.getAttribute('data-flavor') || text(label) || text(card);
      input.setAttribute('aria-label', flavor);
      card.setAttribute('aria-label', flavor);
      card.setAttribute('aria-describedby', 'tca-bab-selection-help');
      var img = card.querySelector('img');
      if (img) {
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
      }
    });

    var flavors = container.querySelector('.cb-flavors');
    if (flavors && !container.querySelector('#tca-bab-selection-help')) {
      var help = el('p', 'visually-hidden', 'Select one or more flavors. The selected flavors are divided evenly across the box.');
      help.id = 'tca-bab-selection-help';
      flavors.insertBefore(help, flavors.firstChild);
    }
  }

  function updateProgress(container) {
    var steps = container.querySelectorAll('.tca-bab-progress-step');
    if (!steps.length) return;
    var selected = getSelectedCount(container);
    var ready = selected > 0 && getAddButton(container) && !getAddButton(container).disabled;
    var done = [true, selected > 0, selected > 0, !!ready];
    Array.prototype.forEach.call(steps, function (step, idx) {
      step.classList.toggle('is-complete', done[idx]);
      step.classList.toggle('is-active', !done[idx] && (idx === 0 || done[idx - 1]));
    });
  }

  function updateSizeCards(container) {
    var size = getSize(container);
    Array.prototype.forEach.call(container.querySelectorAll('.tca-bab-size-cards .tca-bab-choice'), function (card) {
      var selected = card.dataset.value === size;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
  }

  function setTextIfChanged(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function updateDynamicCTA(container) {
    var addBtn = getAddButton(container);
    if (!addBtn) return;
    var selected = getSelectedCount(container);
    var max = getMax(container);
    var size = getSize(container);
    var price = getPrice(container);
    var nextText;
    if (selected === 0 || addBtn.disabled) {
      nextText = 'Choose at least 1 flavor';
      addBtn.setAttribute('aria-disabled', 'true');
    } else {
      nextText = 'Add custom ' + size + '-piece box' + (price ? ' — ' + price : '');
      addBtn.setAttribute('aria-disabled', 'false');
    }
    setTextIfChanged(addBtn, nextText);

    var summary = container.querySelector('#box-summary');
    if (summary && selected > 0) {
      var existing = summary.textContent.replace(/\s+· We’ll divide pieces evenly\.$/i, '');
      setTextIfChanged(summary, existing + ' · We’ll divide pieces evenly.');
    }

    var mobileState = container.querySelector('.tca-bab-mobile-state');
    var mobileCta = container.querySelector('.tca-bab-mobile-cta');
    if (mobileState) {
      setTextIfChanged(mobileState, selected > 0
        ? selected + ' of ' + max + ' flavors selected · ' + size + ' pieces' + (price ? ' · ' + price : '')
        : 'Choose up to ' + max + ' flavors');
    }
    if (mobileCta) {
      setTextIfChanged(mobileCta, selected > 0 ? 'Add to cart' : 'Choose flavors');
      mobileCta.disabled = false;
    }
  }

  function updateAll(container) {
    optimizeThumbnails(container);
    cleanupAccessibility(container);
    updateSizeCards(container);
    updateProgress(container);
    updateDynamicCTA(container);
  }

  function init(container) {
    if (!container || container.__tcaRevenueEnhanced) return;
    container.__tcaRevenueEnhanced = true;
    addHero(container);
    addSizeCards(container);
    addAssortments(container);
    addGiftGuidance(container);
    addMobileSummary(container);
    updateAll(container);

    var select = container.querySelector('#box-size-select');
    if (select) select.addEventListener('change', function () { window.setTimeout(function () { updateAll(container); }, 0); });

    var scheduled = false;
    function scheduleUpdate() {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () {
        scheduled = false;
        updateAll(container);
      }, 40);
    }

    var observer = new MutationObserver(scheduleUpdate);
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class', 'checked', 'src'] });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.custom-box-builder'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
